import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import {
  basicAvailableUsdAtom,
  formatQuantity,
  formatUsd,
} from '@root/entities/basic'
import { ControlsQuickOption } from '@root/features/trade/trading-controls/ui/common'
import { TradeSettingsType } from '@root/features/trade/trading-controls/ui/common/trading-settings-type'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import {
  BUY_PRESETS_USD,
  SELL_PRESETS_PERCENT,
  basicAmountRawAtom,
  basicDirectionAtom,
  basicPositionAtom,
  basicSellPercentAtom,
  basicValidationAtom,
  setBasicAmountRawAction,
  setBasicDirectionAction,
  setBasicMaximumAmountAction,
  setBasicSellPercentAction,
  setBasicTokenAction,
  submitBasicTradeModel,
} from '../atoms/reatom-basic-panel'

import { BasicCashHeader } from './basic-cash-header'

type Props = {
  token: { network: string; address: string; symbol: string } | undefined
  onDeposit: () => void
}

/**
 * Buying and selling with a balance we hold.
 *
 * Deliberately smaller than the advanced panel beside it: no wallet, no
 * slippage, no gas, no order type. Somebody in Basic has one balance and one
 * decision — how much — and every control this screen does not have is a
 * decision they do not have to make.
 */
export const BasicPanel: React.FC<Props> = ({ token, onDeposit }) => {
  const { t } = useTranslation()

  const [direction] = useAtom(basicDirectionAtom)
  const [amountRaw] = useAtom(basicAmountRawAtom)
  const [percent] = useAtom(basicSellPercentAtom)
  const [validation] = useAtom(basicValidationAtom)
  const [position] = useAtom(basicPositionAtom)
  const [available] = useAtom(basicAvailableUsdAtom)
  const [submitState] = useAtom(submitBasicTradeModel)

  const setDirection = useAction(setBasicDirectionAction)
  const setAmountRaw = useAction(setBasicAmountRawAction)
  const setPercent = useAction(setBasicSellPercentAction)
  const setMaximum = useAction(setBasicMaximumAmountAction)
  const setToken = useAction(setBasicTokenAction)
  const submit = useAction(submitBasicTradeModel.execute)

  useEffect(() => {
    if (token) {
      setToken(token)
    }
  }, [token, setToken])

  const availableUsd = pipe(
    available,
    AsyncStateN.getOrElseW(() => undefined),
  )

  const submitting = AsyncStateN.isLoading(submitState)
  const buying = direction === 'buy'

  return (
    <Root>
      <BasicCashHeader onDeposit={onDeposit} />

      <TradeSettingsType
        swapDirection={direction}
        buyLabel={t('trading.buy')}
        sellLabel={t('trading.sell')}
        onChange={setDirection}
      />

      {buying ? (
        <>
          <Input
            numeric
            size="m"
            placeholder="0"
            decimalScale={2}
            value={amountRaw}
            onChange={(event) => setAmountRaw(event.target.value)}
            startContent={<FieldLabel>{t('trading.order.amount')}</FieldLabel>}
            endContent={<FieldLabel>$</FieldLabel>}
          />

          <Presets>
            {BUY_PRESETS_USD.map((preset) => (
              <ControlsQuickOption
                key={preset}
                title={`$${preset}`}
                active={amountRaw === String(preset)}
                onClick={() => setAmountRaw(String(preset))}
              />
            ))}
          </Presets>

          <Footnote>
            <span>
              {availableUsd === undefined
                ? ''
                : t('basic.available', { amount: formatUsd(availableUsd) })}
            </span>
            <MaxButton type="button" onClick={setMaximum}>
              {t('basic.max')}
            </MaxButton>
          </Footnote>
        </>
      ) : (
        <>
          <Presets>
            {SELL_PRESETS_PERCENT.map((preset) => (
              <ControlsQuickOption
                key={preset}
                title={`${preset}%`}
                active={percent === preset}
                onClick={() => setPercent(preset)}
              />
            ))}
          </Presets>

          <Footnote>
            {position ? (
              <span>
                {t('basic.you_hold', {
                  amount: formatQuantity(position.quantity),
                  symbol: position.symbol,
                })}
              </span>
            ) : (
              <span>{t('basic.nothing_held')}</span>
            )}
            {position?.valueUsd === undefined ? undefined : (
              <span>{formatUsd(position.valueUsd)}</span>
            )}
          </Footnote>
        </>
      )}

      <Button
        variant="solid"
        color={buying ? 'success' : 'error'}
        radius="rounded"
        size="m"
        disabled={validation.kind !== 'ok' || !token}
        busy={submitting}
        onClick={() => submit()}
      >
        {submitLabel({ t, validation, direction, percent, token })}
      </Button>

      {/* Said under the button, where it answers the tap that just failed. */}
      {AsyncStateN.isError(submitState) ? (
        <Refusal>{t('basic.order_refused')}</Refusal>
      ) : undefined}
    </Root>
  )
}

const submitLabel = ({
  t,
  validation,
  direction,
  percent,
  token,
}: {
  t: ReturnType<typeof useTranslation>['t']
  validation: { kind: string }
  direction: string
  percent: number
  token: { symbol: string } | undefined
}) => {
  if (validation.kind === 'no-amount') {
    return t('trading.error.no-amount')
  }

  if (validation.kind === 'not-enough') {
    return t('basic.not_enough')
  }

  if (validation.kind === 'nothing-held') {
    return t('basic.nothing_held')
  }

  return direction === 'buy'
    ? t('trading.buy')
    : t('basic.sell_percent', { percent, symbol: token?.symbol ?? '' })
}

const Root = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;

  background: var(--background-2);
  border-radius: 16px;
`

const Presets = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 6px;
`

const FieldLabel = styled.span`
  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const Footnote = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;

  color: var(--text-11);
  font-size: 12px;
  font-weight: 500;
`

const MaxButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;

  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`

const Refusal = styled.span`
  color: var(--error-9, var(--text-11));
  font-size: 12px;
  font-weight: 500;
`
